/* eslint-disable @typescript-eslint/no-explicit-any */

import { Component, inject, input, isDevMode } from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Chart } from "chart.js";
import * as htmlToImage from "html-to-image";

import { AdaaHelper, printHtml } from "../../../core/utils";
import { Constants } from "../../constants/constants";
import { Language } from "../../constants/enums";
import { CsvExportService } from "../../services";

@Component({
  selector: "adaa-chart-action-buttons",
  imports: [TranslatePipe],
  templateUrl: "./chart-action-buttons.html",
  styleUrl: "./chart-action-buttons.scss",
})
export class ChartActionButtonsComponent {
  private readonly _translateService = inject(TranslateService);
  private readonly _csvExportService = inject(CsvExportService);

  elementId = input.required<string>();
  elementIds = input<string[]>();
  chartTitles = input<string[]>();
  extraContent = input<string[]>();
  title = input<string>();
  csvChartInfo = input<string>();
  csvRowHeaders = input<string[]>();
  enableDownloadImage = input<boolean>(true);
  enableDownloadPDF = input<boolean>(true);
  enableDownloadCSV = input<boolean>(true);
  /**
   * "canvas" reads the target through the Chart.js instance behind a <canvas>.
   * "element" rasterises any DOM node instead, for charts drawn as plain markup.
   */
  exportMode = input<"canvas" | "element">("canvas");
  /** Rows to write when exporting CSV in "element" mode, where there is no chart to read. */
  csvRows = input<string[][]>();

  public exportPDF() {
    if (this.exportMode() === "element") {
      this._exportElementPDF();
      return;
    }

    return this._exportCanvasPDF();
  }

  private _exportCanvasPDF() {
    const injectStyles = `
      body {
        direction: ${AdaaHelper.getCurrentLang() === Language.Arabic ? "rtl" : "ltr"};
        margin: 0;
        padding: 0;
      }
      .print-container {
        text-align: center;
        margin: 50px 0;
      }
      .page-title{
        margin: 20px 0;
      }
      .chart-title{
        margin-top: 1000px 0;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `;

    const parentDiv = document.querySelector(".sys-layout--topbar");
    let canvasDataURL;

    if (parentDiv) {
      const secondChild = parentDiv.children[1];
      if (secondChild) {
        parentDiv.removeChild(secondChild);
      }
    }

    let printContents = `
    <div class="govbar">${parentDiv?.outerHTML}</div>
    ${this.title() ? `<div class="page-title">${this.title()}</div>` : ""}
  `;

    if (this.elementIds()) {
      for (let index = 0; index < this.elementIds()!.length; index++) {
        const element = this.elementIds()![index];
        const url = this._getCanvasDataURL(element);
        if (this.chartTitles()) {
          if (this.chartTitles()![index] && AdaaHelper.isDefinedAndNotEmpty(this.chartTitles()![index])) {
            printContents += `
            <div class="chart-title">
              <p>${this.chartTitles()![index]}</p>
            </div>`;
          }
        }
        printContents += `
        <div class="print-container">
          <img src="${url}" alt="Canvas Content" />
        </div>
        `;
        if (this.extraContent()) {
          if (this.extraContent()![index] && AdaaHelper.isDefinedAndNotEmpty(this.extraContent()![index])) {
            printContents += `${this.extraContent()![index]}`;
          }
        }
      }
    } else {
      canvasDataURL = this._getCanvasDataURL(this.elementId());
      if (!canvasDataURL) return;
      printContents += `
        <div class="print-container">
          <img src="${canvasDataURL}" alt="Canvas Content" />
        </div>
      `;
      const canvas = document.getElementById(this.elementId()) as HTMLCanvasElement;
      const chartInstance: any = Chart.getChart(canvas);
      if (chartInstance?.config._config.type === "pie") {
        printContents += this._getStringifiedChartData(canvas) ?? "";
      }
    }

    printHtml(`
      <html>
        <head>
          <title>CHART</title>
          <base href="${document.baseURI}" />
          <style>${injectStyles}</style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
  }

  public exportImage() {
    if (this.exportMode() === "element") {
      this._exportElementImage();
      return;
    }

    return this._exportCanvasImage();
  }

  private _exportCanvasImage() {
    const canvas = document.getElementById(this.elementId()) as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    const tempCanvas = document.createElement("canvas");
    const context = tempCanvas.getContext("2d");

    if (context) {
      const originalWidth = canvas.width;
      const originalHeight = canvas.height;

      const scaleFactor = 2;
      const padding = 50 * scaleFactor;
      tempCanvas.width = originalWidth * scaleFactor + padding * 2;
      tempCanvas.height = originalHeight * scaleFactor + padding * 2;

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      const offsetX = (tempCanvas.width - originalWidth * scaleFactor) / 2;
      const offsetY = (tempCanvas.height - originalHeight * scaleFactor) / 2;

      context.font = "20px Open Sans";
      context.fillStyle = "#000000";

      let textX;
      if (AdaaHelper.getCurrentLang() === Language.Arabic) {
        context.textAlign = "right";
        textX = tempCanvas.width - padding;
      } else {
        context.textAlign = "left";
        textX = padding;
      }

      const textY = padding / 2;

      context.fillText(this.csvChartInfo() ?? "", textX, textY);
      context.fillText(this.title() ?? "", textX, textY + 40);

      context.drawImage(
        canvas,
        0,
        0,
        originalWidth,
        originalHeight,
        offsetX,
        offsetY + 20,
        originalWidth * scaleFactor,
        originalHeight * scaleFactor
      );

      const chartInstance: any = Chart.getChart(canvas);
      if (chartInstance?.config._config.type === "pie") {
        const data = this._getStringifiedChartData(canvas) ?? "";

        context.font = `${11 * scaleFactor}px Arial`;
        context.fillStyle = "#706666";
        context.textAlign = "center";

        const textX = tempCanvas.width / 2;
        const textY = tempCanvas.height - 100 / 2;
        context.fillText(data!, textX, textY);
      }

      const imageData = tempCanvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.href = imageData;
      downloadLink.download = "chart.png";
      downloadLink.click();
    }
  }

  public exportCSV(): void {
    const rows = this.csvRows();
    if (rows) {
      this._exportProvidedCSV(rows);
      return;
    }

    return this._exportCanvasCSV();
  }

  private _exportCanvasCSV(): void {
    const ids = Array.isArray(this.elementIds()) ? this.elementIds() : [this.elementId()];

    const lines: string[] = [];

    const delimiter = "\t";

    ids?.forEach((canvasId) => {
      const canvas = document.getElementById(canvasId!) as HTMLCanvasElement | null;
      if (!canvas) {
        return;
      }

      const chartInstance = Chart.getChart(canvas);
      if (!chartInstance) {
        return;
      }

      const lang = AdaaHelper.getFieldLanguage("name");
      const entity = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
        type: "prop",
        property: lang,
      }) as string;

      lines.push("");
      lines.push(entity);
      lines.push(this.csvChartInfo() ?? "");

      const chartData = chartInstance.data;
      if (chartData?.labels && chartData.datasets) {
        if (this.csvRowHeaders() && this.csvRowHeaders()!.length > 0) {
          lines.push(
            this.csvRowHeaders()!
              .map((e) => {
                const translated = this._translateService.instant(e);
                return this._csvExportService.escape(translated ?? e, delimiter);
              })
              .join("\t")
          );
        }

        chartData.labels.forEach((label, i) => {
          const rowValues: string[] = [];
          rowValues.push(
            this._csvExportService.escape(AdaaHelper.isDefinedAndNotEmpty(label) ? `${label}` : "", delimiter)
          );
          chartData.datasets.forEach((dataset) => {
            const value = dataset.data[i];
            rowValues.push(
              this._csvExportService.escape(AdaaHelper.isDefinedAndNotEmpty(value) ? `${value}` : "", delimiter)
            );
          });
          lines.push(rowValues.join("\t"));
        });
      }
    });

    this._csvExportService.saveCsv({
      fileName: `charts_export_${new Date().toISOString()}`,
      lines,
      delimiter: "\t",
      encoding: "utf-16le",
    });
  }

  private _getStringifiedChartData(canvas: HTMLCanvasElement) {
    const chartInstance = Chart.getChart(canvas);
    if (!chartInstance?.data) {
      return;
    }
    const chartData = chartInstance.data;
    return chartData.labels?.map((label, index) => `${label}: ${chartData.datasets[0].data[index]}`).join("\t ");
  }

  private _exportElementImage() {
    const element = document.getElementById(this.elementId());
    if (!element) return;

    htmlToImage
      .toPng(element, { backgroundColor: "#ffffff", pixelRatio: 2 })
      .then((dataUrl) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = dataUrl;
        downloadLink.download = "chart.png";
        downloadLink.click();
      })
      .catch(() => this._warnExportFailed());
  }

  private _exportElementPDF() {
    const element = document.getElementById(this.elementId());
    if (!element) return;

    htmlToImage
      .toSvg(element)
      .then((dataUrl) => this._printImage(dataUrl))
      .catch(() => this._warnExportFailed());
  }

  private _printImage(dataUrl: string) {
    const injectStyles = `
      body {
        direction: ${AdaaHelper.getCurrentLang() === Language.Arabic ? "rtl" : "ltr"};
        margin: 0;
        padding: 0;
      }
      .print-container {
        text-align: center;
        margin: 50px 0;
      }
      .page-title {
        margin: 20px 0;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `;

    printHtml(`
      <html>
        <head>
          <title>CHART</title>
          <style>${injectStyles}</style>
        </head>
        <body>
          ${this.title() ? `<div class="page-title">${this.title()}</div>` : ""}
          <div class="print-container">
            <img src="${dataUrl}" alt="Chart Content" />
          </div>
        </body>
      </html>
    `);
  }

  private _exportProvidedCSV(rows: string[][]): void {
    const delimiter = "\t";
    const lang = AdaaHelper.getFieldLanguage("name");
    const entity = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      type: "prop",
      property: lang,
    }) as string;

    const lines: string[] = ["", entity, this.csvChartInfo() ?? ""];

    if (this.csvRowHeaders() && this.csvRowHeaders()!.length > 0) {
      lines.push(
        this.csvRowHeaders()!
          .map((e) => {
            const translated = this._translateService.instant(e);
            return this._csvExportService.escape(translated ?? e, delimiter);
          })
          .join(delimiter)
      );
    }

    rows.forEach((row) => {
      lines.push(row.map((value) => this._csvExportService.escape(value ?? "", delimiter)).join(delimiter));
    });

    this._csvExportService.saveCsv({
      fileName: `charts_export_${new Date().toISOString()}`,
      lines,
      delimiter,
      encoding: "utf-16le",
    });
  }

  private _warnExportFailed() {
    if (!isDevMode()) return;
    // eslint-disable-next-line no-console
    console.warn("adaa-chart-action-buttons: could not rasterise the element for export");
  }

  private _getCanvasDataURL = (elementId: string): string | null => {
    const canvas = document.getElementById(elementId) as HTMLCanvasElement | null;
    if (!canvas) return null;
    return canvas.toDataURL();
  };
}
